import { zodResolver } from '@hookform/resolvers/zod';
import { QuoteShareResponse, RecordShareBaseParams } from '@monetize/types/app';
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { handleApiErrorToast } from '../../../api/axios';
import {
  useUpdateAccountNameOnQuoteShare,
  useUpdatePoNumberOnQuoteShare,
  useUpdateVatNumberOnQuoteShare,
} from '../../../api/shareService';
import {
  MButton,
  MCenterModal,
  MFormField,
  MInput,
  MStack,
} from '../../../components/Monetize';
import { logger } from '../../../services/logger';
import Sentry from '../../../services/sentry';
import {
  getRecordShareCaptureContext,
  getRecordShareErrorLogContext,
} from '../../../utils/recordShareLogging';

function buildFormSchema({
  accountNameEditable,
  poEditable,
  vatEditable,
  poRequired,
  vatRequired,
}: {
  accountNameEditable: boolean;
  poEditable: boolean;
  vatEditable: boolean;
  poRequired: boolean;
  vatRequired: boolean;
}) {
  return z
    .object({
      accountName: z
        .string()
        .max(255, 'Legal Name must be less than 255 characters')
        .nullish(),
      purchaseOrderNumber: z
        .string()
        .max(255, 'PO Number must be less than 255 characters')
        .nullish(),
      vatNumber: z
        .string()
        .max(255, 'VAT Number must be less than 255 characters')
        .nullish(),
    })
    .superRefine((val, ctx) => {
      if (accountNameEditable && !val.accountName?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'Legal Name is required',
          path: ['accountName'],
        });
      }
      if (poEditable && poRequired && !val.purchaseOrderNumber?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'PO Number is required',
          path: ['purchaseOrderNumber'],
        });
      }
      if (vatEditable && vatRequired && !val.vatNumber?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'VAT Number is required',
          path: ['vatNumber'],
        });
      }
    });
}

type FormData = z.infer<ReturnType<typeof buildFormSchema>>;

interface QuoteShareCompanyDetailsEditModalProps {
  quoteData: QuoteShareResponse;
  authParams: RecordShareBaseParams;
  accountNameEditable: boolean;
  poEditable: boolean;
  vatEditable: boolean;
  poRequired: boolean;
  vatRequired: boolean;
  onClose: () => void;
}

export const QuoteShareCompanyDetailsEditModal = ({
  quoteData,
  authParams,
  accountNameEditable,
  poEditable,
  vatEditable,
  poRequired,
  vatRequired,
  onClose,
}: QuoteShareCompanyDetailsEditModalProps) => {
  const { mutateAsync: updateAccountName, isPending: isSavingAccountName } =
    useUpdateAccountNameOnQuoteShare();
  const { mutateAsync: updatePoNumber, isPending: isSavingPo } =
    useUpdatePoNumberOnQuoteShare();
  const { mutateAsync: updateVatNumber, isPending: isSavingVat } =
    useUpdateVatNumberOnQuoteShare();

  const isSaving = isSavingAccountName || isSavingPo || isSavingVat;

  const formSchema = useMemo(
    () =>
      buildFormSchema({
        accountNameEditable,
        poEditable,
        vatEditable,
        poRequired,
        vatRequired,
      }),
    [accountNameEditable, poEditable, vatEditable, poRequired, vatRequired],
  );

  const { control, handleSubmit, formState } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountName: quoteData.quote.accountName || '',
      purchaseOrderNumber: quoteData.quote.purchaseOrderNumber || '',
      vatNumber: quoteData.quote.vatNumber || '',
    },
  });
  const { errors } = formState;

  async function handleSave(data: FormData) {
    try {
      // Perform updates sequentially to avoid race conditions
      const tasks: Array<() => Promise<unknown>> = [];

      const nextAccountName = data.accountName?.trim() || '';
      if (
        accountNameEditable &&
        nextAccountName !== (quoteData.quote.accountName || '')
      ) {
        tasks.push(() =>
          updateAccountName({ authParams, accountName: nextAccountName }),
        );
      }

      const nextPoNumber = data.purchaseOrderNumber?.trim() || null;
      if (
        poEditable &&
        nextPoNumber !== (quoteData.quote.purchaseOrderNumber || null)
      ) {
        tasks.push(() =>
          updatePoNumber({ authParams, purchaseOrderNumber: nextPoNumber }),
        );
      }

      const nextVatNumber = data.vatNumber?.trim() || null;
      if (
        vatEditable &&
        nextVatNumber !== (quoteData.quote.vatNumber || null)
      ) {
        tasks.push(() =>
          updateVatNumber({ authParams, vatNumber: nextVatNumber }),
        );
      }

      for (const task of tasks) {
        await task();
      }
      onClose();
    } catch (ex) {
      const logContext = getRecordShareErrorLogContext(authParams, ex);
      Sentry.logger.error(
        'QuoteSharePage - company details update failed',
        logContext,
      );
      Sentry.captureException(
        ex,
        getRecordShareCaptureContext(logContext, {
          type: 'QUOTE_SHARE',
          action: 'UPDATE_COMPANY_DETAILS',
        }),
      );
      handleApiErrorToast(ex);
      logger.error(ex);
    }
  }

  return (
    <MCenterModal
      size="xl"
      isOpen
      onClose={onClose}
      modalTitle="Edit Company and Order Details"
      closeOnEsc={!isSaving}
      closeOnOverlayClick={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          <MButton
            onClick={() => onClose()}
            variant="cancel"
            minW="auto"
            isDisabled={isSaving}
          >
            Cancel
          </MButton>
          <MButton
            variant="primary"
            isLoading={isSaving}
            isDisabled={isSaving}
            form="company-details-form"
            type="submit"
            minW="auto"
          >
            Save
          </MButton>
        </MStack>
      )}
    >
      <form
        id="company-details-form"
        onSubmit={handleSubmit(handleSave, (err) => logger.error(err))}
      >
        {accountNameEditable && (
          <MFormField error={errors?.accountName} label="Legal Name" isRequired>
            <Controller
              name="accountName"
              control={control}
              render={({ field }) => (
                <MInput {...field} value={field.value || ''} />
              )}
            />
          </MFormField>
        )}

        {vatEditable && (
          <MFormField
            error={errors?.vatNumber}
            label="VAT Number"
            isRequired={vatRequired}
            mt={accountNameEditable ? 4 : 0}
          >
            <Controller
              name="vatNumber"
              control={control}
              render={({ field }) => (
                <MInput {...field} value={field.value || ''} />
              )}
            />
          </MFormField>
        )}

        {poEditable && (
          <MFormField
            error={errors?.purchaseOrderNumber}
            label="PO Number"
            isRequired={poRequired}
            mt={accountNameEditable || vatEditable ? 4 : 0}
          >
            <Controller
              name="purchaseOrderNumber"
              control={control}
              render={({ field }) => (
                <MInput {...field} value={field.value || ''} />
              )}
            />
          </MFormField>
        )}
      </form>
    </MCenterModal>
  );
};
